import { fullName } from "odyssey-shared";
import type { ChatLine } from "./store.ts";

/**
 * The name a chat line shows for a character (ADR-068's amendment of 2026-10-03, "chat lines show the first name";
 * the author's ruling: "in chat, show the players name only (no family)"): the first name alone, with the full name
 * in the name's tooltip. A first name is unique within its family and not across the game (ADR-065 decision 1), so
 * two characters named in one log can share one; while they do, each of their lines shows the full name, and goes
 * back to the first name once the line that shared it has left the log. Every line of the log that names a
 * character words the name here. What a whisper, a block or a mute is addressed by is not this file's: the page
 * still sends both names.
 */

/**
 * A character's two names, as a line, a lookup and the wire hold them.
 */
export interface ChatName {
  readonly firstName: string;
  readonly familyName: string;
}

/**
 * The first names two or more different characters named in the log bear, so their lines must show the family too.
 * The whole kept log is read, not the shown tab's lines, so a name reads the same under every tab.
 *
 * @param lines The log's lines; one that names no character is passed over.
 * @param beside A name about to be shown that is not yet in the log, as a system line's is, or nothing.
 * @returns The shared first names in lower case.
 * @example
 * sharedFirstNames([{ id: 1, kind: "local", firstName: "Mira", familyName: "Thornwood", text: "hi" }, { id: 2, kind: "local", firstName: "Mira", familyName: "Qasim", text: "hi" }]);
 * // Set { "mira" }
 */
export function sharedFirstNames(lines: readonly ChatLine[], beside?: ChatName): ReadonlySet<string> {
  const families = new Map<string, string>();
  const shared = new Set<string>();
  /**
   * Counts one name: a first name seen before under another family is shared.
   *
   * @param name The name.
   */
  const count = (name: ChatName): void => {
    const first = name.firstName.toLowerCase();
    const family = name.familyName.toLowerCase();
    const seen = families.get(first);
    if (seen === undefined) {
      families.set(first, family);
    } else if (seen !== family) {
      shared.add(first);
    }
  };
  for (const line of lines) {
    if ("firstName" in line) {
      count(line);
    }
  }
  if (beside !== undefined) {
    count(beside);
  }
  return shared;
}

/**
 * The name a chat line shows for a character.
 *
 * @param name The character's two names.
 * @param shared The first names the log shares (`sharedFirstNames`).
 * @returns The first name, or the full name when its first name is shared; a character with no family has its
 *   first name either way.
 * @example
 * chatName({ firstName: "Kaelen", familyName: "Ashgrove" }, new Set());
 * // "Kaelen"
 */
export function chatName(name: ChatName, shared: ReadonlySet<string>): string {
  return shared.has(name.firstName.toLowerCase()) ? fullName(name.firstName, name.familyName) : name.firstName;
}
